import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { TableSortPopover } from '@/features/shared/components/tableSystem/TableSortPopover';
import { buildToolbarIconButtonSx } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { componentSizes } from '@/theme/componentSizes';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Panel } from '@/ui/composites';
import { Box, Skeleton, useTheme } from '@/ui/primitives';
import { TablePaginationFooter } from '@/ui/primitives/TablePaginationFooter';
import { EntityDailyHistoryExpandControls } from './EntityDailyHistoryExpandControls';
import { collectExpandableRowIds } from './EntityDailyHistoryExpandControls.utils';
import {
  DAILY_STATUS_BLOCK_SIZE,
  HISTORY_DEFAULT_SORT_DIRECTION,
  HISTORY_NAME_COLUMN_COMPRESSED_MIN_WIDTH_PX,
  HISTORY_NAME_COLUMN_DEFAULT_WIDTH_PX,
  HISTORY_NAME_COLUMN_MAX_WIDTH_RATIO,
  HISTORY_NAME_COLUMN_MIN_WIDTH_PX,
  HISTORY_PAGE_SIZE_OPTIONS,
  HISTORY_STATUS_CELL_HORIZONTAL_PADDING_PX,
  HISTORY_STATUS_GRID_GAP_PX,
  HISTORY_TOOLBAR_SKELETON,
} from './EntityDailyHistoryPanel.constants';
import {
  type DailyHistoryCell,
  type EntityDailyHistoryPanelProps,
  type EntityDailyHistoryRow,
} from './EntityDailyHistoryPanel.types';
import {
  normalizeEntityDailyHistoryRows,
  resolveEntityDailyHistoryNameColumnWidth,
  resolveEntityDailyHistoryDayKeys,
} from './EntityDailyHistoryPanel.utils';
import { EntityDailyHistoryPanelBody } from './EntityDailyHistoryPanelBody';
import { EntityDailyHistoryPanelHeader } from './EntityDailyHistoryPanelHeader';
import { useEntityDailyHistoryNameColumnResize } from './useEntityDailyHistoryNameColumnResize';
import { useEntityDailyHistoryPanelController } from './useEntityDailyHistoryPanelController';
import { useEntityDailyHistoryPanelMeasurements } from './useEntityDailyHistoryPanelMeasurements';

const HISTORY_PAGINATION_SKELETON_WIDTH = '380px';
const HISTORY_PAGINATION_SKELETON_RADIUS = '999px';
export const EntityDailyHistoryPanel = ({
  title,
  dateRangeLabel = null,
  dayKeys,
  entityLabel,
  activityLabel,
  rows,
  loading = false,
  knownEmpty = false,
  showErrorState = false,
  noRowsText,
  defaultSortKey,
  defaultSortDirection = HISTORY_DEFAULT_SORT_DIRECTION,
  sortKey: controlledSortKey,
  sortDirection: controlledSortDirection,
  sortOptions,
  onSortChange: onControlledSortChange,
  getSortValue,
  page: controlledPage,
  pageSize: controlledPageSize,
  totalCount: controlledTotalCount,
  onPageChange: onControlledPageChange,
  onPageSizeChange: onControlledPageSizeChange,
  expandedRowIds: controlledExpandedRowIds,
  onExpandedRowIdsChange,
  extraToolbarContent = null,
  entityHeaderContent = null,
  hidePagination = false,
  toolbarDisabled = false,
  linksDisabled = false,
  nameColumnMinWidthPx = HISTORY_NAME_COLUMN_MIN_WIDTH_PX,
  nameColumnDefaultWidthPx = HISTORY_NAME_COLUMN_DEFAULT_WIDTH_PX,
  nameColumnMaxWidthRatio = HISTORY_NAME_COLUMN_MAX_WIDTH_RATIO,
}: EntityDailyHistoryPanelProps): ReactElement => {
  const theme = useTheme();
  const toolbarControlSize = 32;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const [hoverTarget, setHoverTarget] = useState<'sort' | 'reset' | null>(null);
  const resolvedDefaultSortKey = defaultSortKey ?? 'name';
  const dayHeaderGridRef = useRef<HTMLDivElement | null>(null);
  const panelTableContainerRef = useRef<HTMLDivElement | null>(null);
  const nameColumnHeaderCellRef = useRef<HTMLTableCellElement | null>(null);
  const [dayHeaderGridWidth, setDayHeaderGridWidth] = useState(0);
  const [panelTableWidth, setPanelTableWidth] = useState(0);
  const resolvedSortOptions = sortOptions ?? [{ key: resolvedDefaultSortKey, label: entityLabel }];
  const canInteractWithToolbar = !toolbarDisabled;
  const showHeaderLoading = false;
  const preserveWindowScroll = (action: () => void): void => {
    if (typeof window === 'undefined') {
      action();
      return;
    }
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    action();
    window.requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
    });
  };
  const {
    closeSortPopover,
    expandedRowIds,
    handlePageChange,
    handleReset,
    handleRowsPerPageChange,
    handleSortButtonClick,
    handleSortChange,
    hasActiveSort,
    isPaginationControlled,
    page,
    pageSize,
    setExpandedRowIds,
    sortAnchorEl,
    sortDirection,
    sortKey,
    toggleRowExpansion,
  } = useEntityDailyHistoryPanelController({
    controlledExpandedRowIds,
    controlledPage,
    controlledPageSize,
    controlledSortDirection,
    controlledSortKey,
    controlledTotalCount,
    defaultSortDirection,
    onControlledPageChange,
    onControlledPageSizeChange,
    onControlledSortChange,
    onExpandedRowIdsChange,
    preserveWindowScroll,
    resolvedDefaultSortKey,
  });
  const resolvedDayKeys = useMemo(
    () => resolveEntityDailyHistoryDayKeys(rows, dayKeys),
    [dayKeys, rows],
  );
  const sortedRows = useMemo(() => {
    const normalizedRows = normalizeEntityDailyHistoryRows(rows, resolvedDayKeys);
    const resolveSortValue = (row: EntityDailyHistoryRow): string => {
      if (getSortValue) return getSortValue(row, sortKey);
      if (sortKey === 'prefixLabel') return row.prefixLabel ?? '';
      return row.name;
    };
    const sortRowList = (
      inputRows: EntityDailyHistoryRow[],
      nestingLevel: number,
    ): EntityDailyHistoryRow[] => {
      const nextRows = [...inputRows];
      nextRows.sort((left, right) => {
        if (nestingLevel > 0) {
          const leftSortOrder = left.sortOrder ?? Number.POSITIVE_INFINITY;
          const rightSortOrder = right.sortOrder ?? Number.POSITIVE_INFINITY;
          if (leftSortOrder !== rightSortOrder) {
            return leftSortOrder - rightSortOrder;
          }
        }
        const leftValue = resolveSortValue(left);
        const rightValue = resolveSortValue(right);
        const primaryComparison = leftValue.localeCompare(rightValue, undefined, {
          sensitivity: 'base',
        });
        if (primaryComparison !== 0) {
          return sortDirection === 'asc' ? primaryComparison : primaryComparison * -1;
        }
        const leftPrefix = left.prefixLabel ?? '';
        const rightPrefix = right.prefixLabel ?? '';
        const prefixComparison = leftPrefix.localeCompare(rightPrefix, undefined, {
          sensitivity: 'base',
        });
        if (prefixComparison !== 0) {
          return sortDirection === 'asc' ? prefixComparison : prefixComparison * -1;
        }
        const fallbackComparison = left.name.localeCompare(right.name, undefined, {
          sensitivity: 'base',
        });
        return sortDirection === 'asc' ? fallbackComparison : fallbackComparison * -1;
      });
      return nextRows.map((row) => ({
        ...row,
        childRows: row.childRows ? sortRowList(row.childRows, nestingLevel + 1) : undefined,
      }));
    };
    return sortRowList(normalizedRows, 0);
  }, [getSortValue, resolvedDayKeys, rows, sortDirection, sortKey]);
  const days = useMemo<DailyHistoryCell[]>(() => {
    if (sortedRows.length > 0) {
      const rowDays = sortedRows[0].dailyHistory;
      if (rowDays.length > 0) {
        return rowDays;
      }
    }

    return resolvedDayKeys.map((dayKey) => ({
      day: new Date(`${dayKey}T00:00:00.000Z`),
      status: null,
      statuses: [],
      details: [],
    }));
  }, [resolvedDayKeys, sortedRows]);
  useEffect(() => {
    if (isPaginationControlled) {
      return;
    }

    const maxPage = Math.max(0, Math.ceil(sortedRows.length / pageSize) - 1);
    if (page > maxPage) {
      handlePageChange(null, maxPage);
    }
  }, [handlePageChange, isPaginationControlled, page, pageSize, sortedRows.length]);
  const paginatedRows = useMemo(() => {
    if (isPaginationControlled) {
      return sortedRows;
    }

    const startIndex = page * pageSize;
    return sortedRows.slice(startIndex, startIndex + pageSize);
  }, [isPaginationControlled, page, pageSize, sortedRows]);
  const expandableRowIds = useMemo(() => collectExpandableRowIds(paginatedRows), [paginatedRows]);
  const canExpandAll = expandableRowIds.some((rowId) => !expandedRowIds.has(rowId));
  const canCollapseAll = expandableRowIds.some((rowId) => expandedRowIds.has(rowId));
  const expandAllRows = useCallback((): void => {
    setExpandedRowIds(new Set([...expandedRowIds, ...expandableRowIds]));
  }, [expandableRowIds, expandedRowIds, setExpandedRowIds]);
  const collapseAllRows = useCallback((): void => {
    const nextExpandedRowIds = new Set(expandedRowIds);
    expandableRowIds.forEach((rowId) => {
      nextExpandedRowIds.delete(rowId);
    });
    setExpandedRowIds(nextExpandedRowIds);
  }, [expandableRowIds, expandedRowIds, setExpandedRowIds]);
  const expandCollapseControls =
    expandableRowIds.length > 0 ? (
      <EntityDailyHistoryExpandControls
        canCollapseAll={canCollapseAll}
        canExpandAll={canExpandAll}
        entityLabel={entityLabel}
        onCollapseAll={collapseAllRows}
        onExpandAll={expandAllRows}
      />
    ) : null;
  let resolvedEntityHeaderContent = entityHeaderContent;
  if (expandCollapseControls !== null) {
    resolvedEntityHeaderContent = entityHeaderContent ? (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: s('sm') }}>
        {entityHeaderContent}
        {expandCollapseControls}
      </Box>
    ) : (
      expandCollapseControls
    );
  }
  const clampNameColumnWidth = useCallback(
    (widthPx: number): number => {
      return resolveEntityDailyHistoryNameColumnWidth({
        dayCount: days.length,
        panelWidthPx: panelTableWidth,
        preferredWidthPx: widthPx,
        minWidthPx: nameColumnMinWidthPx,
        compressedMinWidthPx: HISTORY_NAME_COLUMN_COMPRESSED_MIN_WIDTH_PX,
        nameColumnMaxWidthRatio,
        statusBlockSizePx: DAILY_STATUS_BLOCK_SIZE,
        statusColumnGapPx: HISTORY_STATUS_GRID_GAP_PX,
        statusCellHorizontalPaddingPx: HISTORY_STATUS_CELL_HORIZONTAL_PADDING_PX,
      });
    },
    [days.length, nameColumnMaxWidthRatio, nameColumnMinWidthPx, panelTableWidth],
  );
  const {
    handleNameColumnResizeKeyDown,
    handleNameColumnResizeStart,
    nameColumnWidth,
    resizedNameColumnWidthPx,
    setResizedNameColumnWidthPx,
  } = useEntityDailyHistoryNameColumnResize({
    clampNameColumnWidth,
    nameColumnDefaultWidthPx,
    nameColumnHeaderCellRef,
  });
  const nameColumnWidthPx =
    resizedNameColumnWidthPx === null
      ? clampNameColumnWidth(nameColumnDefaultWidthPx)
      : clampNameColumnWidth(resizedNameColumnWidthPx);
  const { visibleDayHeaderIndices } = useEntityDailyHistoryPanelMeasurements({
    clampNameColumnWidth,
    dayCount: days.length,
    dayHeaderGridRef,
    dayHeaderGridWidth,
    nameColumnWidthPx,
    panelTableWidth,
    panelTableContainerRef,
    setDayHeaderGridWidth,
    setPanelTableWidth,
    setResizedNameColumnWidthPx,
  });
  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="none"
      headerGap="none"
      headerSlot={
        <EntityDailyHistoryPanelHeader
          title={title}
          dateRangeLabel={dateRangeLabel}
          extraToolbarContent={extraToolbarContent}
          loading={showHeaderLoading}
          canInteractWithToolbar={canInteractWithToolbar}
          hasActiveSort={hasActiveSort}
          hoverTarget={hoverTarget}
          toolbarControlSize={toolbarControlSize}
          toolbarIconButtonSx={toolbarIconButtonSx}
          toolbarInactiveIconColor={toolbarInactiveIconColor}
          onSortButtonClick={handleSortButtonClick}
          onReset={handleReset}
          onHoverTargetChange={setHoverTarget}
        />
      }
      sx={{
        backgroundColor: 'background.paper',
        py: s('none'),
        px: s('md'),
      }}
    >
      <EntityDailyHistoryPanelBody
        title={title}
        entityLabel={entityLabel}
        entityHeaderContent={resolvedEntityHeaderContent}
        activityLabel={activityLabel}
        rows={sortedRows}
        loading={loading}
        knownEmpty={knownEmpty}
        showErrorState={showErrorState}
        noRowsText={noRowsText}
        days={days}
        visibleDayHeaderIndices={visibleDayHeaderIndices}
        dayHeaderGridRef={dayHeaderGridRef}
        nameColumnHeaderCellRef={nameColumnHeaderCellRef}
        panelTableContainerRef={panelTableContainerRef}
        nameColumnWidth={nameColumnWidth}
        pageSize={pageSize}
        paginatedRows={paginatedRows}
        expandedRowIds={expandedRowIds}
        linksDisabled={linksDisabled}
        onToggleRowExpansion={toggleRowExpansion}
        onNameColumnResizeStart={handleNameColumnResizeStart}
        onNameColumnResizeKeyDown={handleNameColumnResizeKeyDown}
      />
      <Box>
        {!hidePagination && !showErrorState ? (
          loading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
                backgroundColor: 'background.paper',
                height: `${componentSizes.dataGrid.footerHeight}px`,
                minHeight: `${componentSizes.dataGrid.footerHeight}px`,
                maxHeight: `${componentSizes.dataGrid.footerHeight}px`,
                boxSizing: 'border-box',
                px: s('md'),
                py: s('none'),
                borderTop: 'none',
                position: 'relative',
                '&::before': buildTableDividerPseudoSx('top'),
              }}
            >
              <Skeleton
                variant="rounded"
                width={HISTORY_PAGINATION_SKELETON_WIDTH}
                sx={{
                  height: `${HISTORY_TOOLBAR_SKELETON.height}px`,
                  transform: 'none',
                  borderRadius: HISTORY_PAGINATION_SKELETON_RADIUS,
                }}
              />
            </Box>
          ) : (
            <TablePaginationFooter
              containerSx={{
                borderTop: 'none',
                position: 'relative',
                '&::before': buildTableDividerPseudoSx('top'),
              }}
              count={
                isPaginationControlled
                  ? (controlledTotalCount ?? sortedRows.length)
                  : sortedRows.length
              }
              disabled={loading}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[...HISTORY_PAGE_SIZE_OPTIONS]}
            />
          )
        ) : null}
      </Box>
      <TableSortPopover
        open={canInteractWithToolbar && sortAnchorEl !== null}
        anchorEl={sortAnchorEl}
        onClose={closeSortPopover}
        sortKey={sortKey}
        sortDirection={sortDirection}
        sortOptions={resolvedSortOptions}
        onSortChange={handleSortChange}
        preserveWindowScroll={preserveWindowScroll}
      />
    </Panel>
  );
};
export type { EntityDailyHistoryRow } from './EntityDailyHistoryPanel.types';
